import { DialogContent, Stack } from '@mui/material';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  useMemo,
  memo,
} from 'react';
import { AutomationsForm } from './AutomationsForm';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { useCrmOrgsApiFacade } from '../../apis/facades/useCrmOrgsApiFacade';
import DialogOnCanvas from '../pages/canvas-pages/DialogOnCanvas';
import { ConfirmLeaveWithoutSaveDialog } from '../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { telemetry } from '../../telemetry';
import useSendOpenAutomationEvent from './useSendOpenAutomationEvent';
import { useAutomationsContext } from './AutomationsContext';
import { AutomationDialogActions } from './AutomationDialogActions';
import ImpactAnalysisChat from './impact-analysis-agent/ImpactAnalysisChat';
import { AutomationsFormValidationCtxProvider } from './automation-form-validation/AutomationsFormValidationCtx';
import { useIsAutomationFormValid } from './automation-form-validation/useIsAutomationFormValid';
import { useGetDeployButtonOptions } from './automation-form-validation/deployToFlowHelper';
import { FormulaEditorStateContextProvider } from './FormulaEditorStateContext';
import { ValueFromAnotherObjectEditorStateContextProvider } from './ValueFromAnotherObjectEditorStateContext';
import { SaveOrCreateAutomationParams } from './useAutomationApiPerType';
import { ParsedRecordTypesAndFunnelsObjectData } from '../../apis/facades/types/crmOrgsApiFacade.types';

export type SingleAutomationDialogRef = {
  isPristine: () => boolean;
};

export interface SingleAutomationDialogProps {
  onSaveOrCreate: (
    params: SaveOrCreateAutomationParams,
  ) => Promise<{ versionId?: string; automationId?: string; isError?: string }>;
  onCancel: () => any;
  isOpen: boolean;
  readonly?: boolean;
  crmOrgId: string;
  objectName: ObjectTypeValues;
  filterFunnelIds?: string[];
  isShowWideScreenPopup?: boolean;
}

const SingleAutomationDialog = memo(
  forwardRef<SingleAutomationDialogRef, SingleAutomationDialogProps>(
    (
      {
        isOpen,
        onSaveOrCreate,
        onCancel: closeDialog,
        readonly,
        crmOrgId,
        objectName,
        filterFunnelIds,
        isShowWideScreenPopup,
      },
      ref,
    ) => {
      const [isImpactAnalysisOpen, setIsImpactAnalysisOpen] = useState(false);
      const { automationJson, isObjectless } = useAutomationsContext();
      const [isPristine, setIsPristine] = useState(true);
      const { openConfirm } = useConfirm();
      const [isDoneLoadingFunnelsDataForObject, setIsDoneLoadingFunnelsDataForObject] =
        useState(false);

      const [recordTypesAndFunnelsDataForObject, setRecordTypesAndFunnelsDataForObject] =
        useState<ParsedRecordTypesAndFunnelsObjectData>();

      const { getObjectFunnelsAndRecordTypes } = useCrmOrgsApiFacade();

      useEffect(() => {
        const setData = async () => {
          if (isObjectless) {
            setIsDoneLoadingFunnelsDataForObject(true);
          } else if (objectName) {
            try {
              const parsedDataForObject = await getObjectFunnelsAndRecordTypes({
                crmOrgId,
                objectName,
              });

              setRecordTypesAndFunnelsDataForObject(
                parsedDataForObject ?? {
                  name: objectName,
                  label: objectName,
                  recordTypes: [],
                  funnels: [],
                },
              );
              setIsDoneLoadingFunnelsDataForObject(true);
            } catch (e) {
              setIsDoneLoadingFunnelsDataForObject(false);
              telemetry.captureError(e);
            }
          }
        };
        setData();
      }, [crmOrgId, getObjectFunnelsAndRecordTypes, isObjectless, objectName]);

      const _onCancel = useCallback(async () => {
        if (isPristine) {
          closeDialog();
        } else {
          const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
          if (isConfirmed) {
            closeDialog();
          }
        }
      }, [isPristine, openConfirm, closeDialog]);

      useImperativeHandle(ref, () => {
        return {
          isPristine() {
            return isPristine;
          },
        };
      }, [isPristine]);

      useSendOpenAutomationEvent();

      useEffect(() => {
        if (!isOpen) {
          setIsImpactAnalysisOpen(false);
        }
      }, [isOpen]);

      const isDeployToSfValid = useIsAutomationFormValid();

      const { getDeployButtonOptions } = useGetDeployButtonOptions();
      const deployButtonOptions = useMemo(
        () => getDeployButtonOptions(automationJson, isDeployToSfValid),
        [automationJson, getDeployButtonOptions, isDeployToSfValid],
      );

      const dialogContent = useMemo(
        () => (
          <AutomationsFormValidationCtxProvider
            isDeployToSfValid={isDeployToSfValid}
            {...deployButtonOptions}
          >
            <DialogOnCanvas
              open={isOpen}
              onClose={_onCancel}
              blockFully={!isPristine && !isShowWideScreenPopup}
              forceWideScreen={isShowWideScreenPopup || isImpactAnalysisOpen}
            >
              <Stack flex={1} direction="row" position="relative" overflow="hidden">
                <Stack flex={1} sx={{ overflowX: 'auto' }}>
                  <DialogContent sx={{ paddingTop: 0 }}>
                    <AutomationsForm
                      key={`${automationJson?.automationId}${automationJson?.versionId}`}
                      objectName={objectName}
                      readonly={readonly}
                      crmOrgId={crmOrgId}
                      onBackButtonClick={_onCancel}
                      onPristineChange={setIsPristine}
                      recordTypesAndFunnelsDataForObject={recordTypesAndFunnelsDataForObject}
                      filterFunnelIds={filterFunnelIds}
                      isDoneLoading={isDoneLoadingFunnelsDataForObject}
                    />
                  </DialogContent>
                  <AutomationDialogActions
                    readonly={readonly}
                    onSaveOrCreate={onSaveOrCreate}
                    crmOrgId={crmOrgId}
                    toggleImpactAnalysis={() => setIsImpactAnalysisOpen((v) => !v)}
                    isImpactAnalysisOpen={isImpactAnalysisOpen}
                  />
                </Stack>
                <ImpactAnalysisChat
                  isOpen={isImpactAnalysisOpen}
                  close={() => setIsImpactAnalysisOpen(false)}
                  automation={automationJson}
                  crmOrgId={crmOrgId}
                />
              </Stack>
            </DialogOnCanvas>
          </AutomationsFormValidationCtxProvider>
        ),
        [
          isDeployToSfValid,
          deployButtonOptions,
          isOpen,
          _onCancel,
          isPristine,
          isShowWideScreenPopup,
          isImpactAnalysisOpen,
          objectName,
          readonly,
          crmOrgId,
          setIsPristine,
          recordTypesAndFunnelsDataForObject,
          filterFunnelIds,
          isDoneLoadingFunnelsDataForObject,
          onSaveOrCreate,
          automationJson,
        ],
      );

      return dialogContent;
    },
  ),
);

const SingleAutomationDialogWithProviders = forwardRef<
  SingleAutomationDialogRef,
  SingleAutomationDialogProps
>((props: SingleAutomationDialogProps, ref) => (
  <ValueFromAnotherObjectEditorStateContextProvider>
    <FormulaEditorStateContextProvider>
      <SingleAutomationDialog {...props} ref={ref} />
    </FormulaEditorStateContextProvider>
  </ValueFromAnotherObjectEditorStateContextProvider>
));

export { SingleAutomationDialogWithProviders as SingleAutomationDialog };
